import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AddIcon from '@mui/icons-material/Add';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { toast } from 'react-toastify';
import Header from '../../../components/Header';
import api_service from '../../../network/api_callbacks';

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Textarea' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone / WhatsApp' },
  { value: 'number', label: 'Number' },
  { value: 'select', label: 'Select / Dropdown' },
  { value: 'radio', label: 'Radio' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'rating', label: 'Rating (1–5)' },
  { value: 'file', label: 'File upload (image/video)' },
];

const FIELD_ICONS = [
  { value: 'person', label: 'Person' },
  { value: 'chat', label: 'Chat' },
  { value: 'chart', label: 'Chart' },
  { value: 'bulb', label: 'Lightbulb' },
  { value: 'gamepad', label: 'Gamepad' },
  { value: 'phone', label: 'Phone' },
  { value: 'email', label: 'Email' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'camera', label: 'Camera' },
  { value: 'comment', label: 'Comment' },
  { value: 'star', label: 'Star' },
];

const ICON_COLORS = [
  '#7B08EF',
  '#E91E8C',
  '#2196F3',
  '#4CAF50',
  '#FF9800',
  '#00BCD4',
  '#FFC107',
  '#25D366',
  '#9C27B0',
];

const DEFAULT_THEME = {
  headerTitle: 'SHARE YOUR',
  headerHighlight: 'EXPERIENCE',
  subtitle: 'Help us make Rapchik better!',
  footerText: 'Your feedback can help shape a better Rapchik!',
  submitButtonText: 'SUBMIT FEEDBACK',
  showRewardsBanner: false,
  rewardsBannerText: 'Best ideas can win Rewards, Awards & Recognition Certificate!',
  showNumberedBadges: false,
  showBackButton: true,
  brandName: 'Rapchik',
};

const needsOptions = (type) => ['select', 'radio', 'checkbox'].includes(type);
const supportsMaxLength = (type) => ['text', 'textarea', 'phone'].includes(type);

const createFieldId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `f_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

const newField = (order = 0, overrides = {}) => ({
  id: createFieldId(),
  type: 'text',
  label: '',
  placeholder: '',
  required: false,
  options: [],
  order,
  icon: 'chat',
  iconColor: '#7B08EF',
  badgeNumber: null,
  width: 'full',
  maxLength: null,
  ...overrides,
});

/** Design A — simpler stacked form (first mock) */
const PRESET_A_FIELDS = () => [
  newField(0, {
    type: 'text',
    label: 'Name / Username',
    placeholder: 'Enter your username',
    required: true,
    icon: 'person',
    iconColor: '#7B08EF',
    maxLength: 50,
  }),
  newField(1, {
    type: 'textarea',
    label: '1. How was your overall experience with Rapchik?',
    placeholder: 'Tell us what you think...',
    required: true,
    icon: 'chat',
    iconColor: '#E91E8C',
    badgeNumber: 1,
    maxLength: 250,
  }),
  newField(2, {
    type: 'textarea',
    label: '2. What can we improve to make Rapchik better?',
    placeholder: 'Share your suggestion...',
    required: true,
    icon: 'chart',
    iconColor: '#2196F3',
    badgeNumber: 2,
    maxLength: 250,
  }),
  newField(3, {
    type: 'textarea',
    label: '3. What new feature would you like to see in Rapchik?',
    placeholder: 'Share your idea...',
    required: true,
    icon: 'bulb',
    iconColor: '#4CAF50',
    badgeNumber: 3,
    maxLength: 250,
  }),
  newField(4, {
    type: 'textarea',
    label: '4. What game idea would make Rapchik more fun & exciting?',
    placeholder: 'Share your game idea...',
    required: true,
    icon: 'gamepad',
    iconColor: '#FF9800',
    badgeNumber: 4,
    maxLength: 250,
  }),
  newField(5, {
    type: 'select',
    label: 'Device / OS (Optional)',
    placeholder: 'Android / iOS',
    required: false,
    options: ['Android', 'iOS', 'Android / iOS', 'Other'],
    icon: 'phone',
    iconColor: '#00BCD4',
  }),
  newField(6, {
    type: 'email',
    label: 'Email',
    placeholder: 'Enter your email',
    required: true,
    icon: 'email',
    iconColor: '#FFC107',
  }),
  newField(7, {
    type: 'phone',
    label: 'WhatsApp Number (Optional)',
    placeholder: '10-digit mobile number',
    required: false,
    icon: 'whatsapp',
    iconColor: '#25D366',
    maxLength: 10,
  }),
];

/** Design B — fuller form with upload, side-by-side, rewards (second mock) */
const PRESET_B_FIELDS = () => [
  newField(0, {
    type: 'text',
    label: 'Name / Username',
    placeholder: 'Enter your name or username',
    required: true,
    icon: 'person',
    iconColor: '#7B08EF',
    maxLength: 50,
  }),
  newField(1, {
    type: 'textarea',
    label: '1. How was your overall experience with Rapchik?',
    placeholder: 'Write your feedback...',
    required: true,
    icon: 'chat',
    iconColor: '#E91E8C',
    badgeNumber: 1,
    maxLength: 250,
  }),
  newField(2, {
    type: 'textarea',
    label: '2. What can we improve to make Rapchik better?',
    placeholder: 'Write your suggestion...',
    required: true,
    icon: 'chart',
    iconColor: '#2196F3',
    badgeNumber: 2,
    maxLength: 250,
  }),
  newField(3, {
    type: 'textarea',
    label: '3. What new feature would you like to see in Rapchik?',
    placeholder: 'Write your idea...',
    required: true,
    icon: 'bulb',
    iconColor: '#4CAF50',
    badgeNumber: 3,
    maxLength: 250,
  }),
  newField(4, {
    type: 'textarea',
    label: '4. What game idea would make Rapchik more fun & exciting?',
    placeholder: 'Write your game idea...',
    required: true,
    icon: 'gamepad',
    iconColor: '#FF9800',
    badgeNumber: 4,
    maxLength: 250,
  }),
  newField(5, {
    type: 'file',
    label: 'Screenshot / Screen Recording (Optional)',
    placeholder: 'Upload image or video',
    required: false,
    icon: 'camera',
    iconColor: '#9C27B0',
  }),
  newField(6, {
    type: 'text',
    label: 'Device / OS (Optional)',
    placeholder: 'e.g. Android 13, iOS 17',
    required: false,
    icon: 'phone',
    iconColor: '#00BCD4',
    width: 'half',
    maxLength: 50,
  }),
  newField(7, {
    type: 'email',
    label: 'Email (For winner communication)',
    placeholder: 'Enter your email',
    required: true,
    icon: 'email',
    iconColor: '#FFC107',
    width: 'half',
  }),
  newField(8, {
    type: 'phone',
    label: 'WhatsApp Number (Optional)',
    placeholder: '10-digit mobile number',
    required: false,
    icon: 'whatsapp',
    iconColor: '#25D366',
    maxLength: 10,
  }),
  newField(9, {
    type: 'textarea',
    label: "Anything else you'd like to share? (Optional)",
    placeholder: 'Write here...',
    required: false,
    icon: 'comment',
    iconColor: '#7B08EF',
    maxLength: 250,
  }),
];

const PRESET_A_THEME = {
  ...DEFAULT_THEME,
  subtitle: 'Help us make Rapchik better!',
  footerText: 'Your feedback can help shape a better Rapchik!',
  showRewardsBanner: false,
  showNumberedBadges: false,
};

const PRESET_B_THEME = {
  ...DEFAULT_THEME,
  subtitle: 'Help us make Rapchik even better!',
  footerText: 'Thank you for helping us build a better Rapchik!',
  showRewardsBanner: true,
  showNumberedBadges: true,
};

function SortableFieldCard({ field, index, total, onChange, onRemove, onMove }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: field.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Paper ref={setNodeRef} style={style} sx={{ p: 2, mb: 2 }} variant="outlined">
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <IconButton size="small" {...attributes} {...listeners} sx={{ cursor: 'grab' }}>
          <DragIndicatorIcon />
        </IconButton>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: 1,
            bgcolor: field.iconColor || '#7B08EF',
            flexShrink: 0,
          }}
        />
        <Typography fontWeight={600} flex={1}>
          Field {index + 1}
        </Typography>
        <IconButton size="small" disabled={index === 0} onClick={() => onMove(index, index - 1)}>
          <ArrowUpwardIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          disabled={index === total - 1}
          onClick={() => onMove(index, index + 1)}
        >
          <ArrowDownwardIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" color="error" onClick={() => onRemove(field.id)}>
          <DeleteIcon />
        </IconButton>
      </Box>

      <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2}>
        <TextField
          select
          label="Type"
          value={field.type}
          onChange={(e) => {
            const nextType = e.target.value;
            const patch = { type: nextType };
            if (nextType === 'phone' && (field.maxLength == null || field.maxLength === '')) {
              patch.maxLength = 10;
            }
            if (!supportsMaxLength(nextType)) {
              patch.maxLength = null;
            }
            onChange(field.id, patch);
          }}
          fullWidth
          size="small"
        >
          {FIELD_TYPES.map((type) => (
            <MenuItem key={type.value} value={type.value}>
              {type.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Label"
          value={field.label}
          onChange={(e) => onChange(field.id, { label: e.target.value })}
          fullWidth
          size="small"
          required
        />
        <TextField
          label="Placeholder"
          value={field.placeholder || ''}
          onChange={(e) => onChange(field.id, { placeholder: e.target.value })}
          fullWidth
          size="small"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(field.required)}
              onChange={(e) => onChange(field.id, { required: e.target.checked })}
            />
          }
          label="Required"
        />
        {supportsMaxLength(field.type) ? (
          <TextField
            label={field.type === 'phone' ? 'Digit limit' : 'Character limit'}
            type="number"
            value={field.maxLength ?? ''}
            onChange={(e) =>
              onChange(field.id, {
                maxLength: e.target.value === '' ? null : Number(e.target.value),
              })
            }
            fullWidth
            size="small"
            helperText={
              field.type === 'phone'
                ? 'WhatsApp/phone: use 10 for Indian mobile numbers'
                : 'Leave empty for no limit (e.g. Name=50, questions=250)'
            }
            inputProps={{ min: 1, max: 10000 }}
          />
        ) : null}
        <TextField
          select
          label="Icon"
          value={field.icon || 'chat'}
          onChange={(e) => onChange(field.id, { icon: e.target.value })}
          fullWidth
          size="small"
        >
          {FIELD_ICONS.map((icon) => (
            <MenuItem key={icon.value} value={icon.value}>
              {icon.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Icon color"
          value={field.iconColor || '#7B08EF'}
          onChange={(e) => onChange(field.id, { iconColor: e.target.value })}
          fullWidth
          size="small"
        >
          {ICON_COLORS.map((color) => (
            <MenuItem key={color} value={color}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box sx={{ width: 16, height: 16, borderRadius: 0.5, bgcolor: color }} />
                {color}
              </Box>
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Badge number"
          type="number"
          value={field.badgeNumber ?? ''}
          onChange={(e) =>
            onChange(field.id, {
              badgeNumber: e.target.value === '' ? null : Number(e.target.value),
            })
          }
          fullWidth
          size="small"
          helperText="Leave empty for no badge (Design B uses 1–4)"
          inputProps={{ min: 1, max: 99 }}
        />
        <TextField
          select
          label="Width"
          value={field.width || 'full'}
          onChange={(e) => onChange(field.id, { width: e.target.value })}
          fullWidth
          size="small"
          helperText="Half = side-by-side with next half field"
        >
          <MenuItem value="full">Full width</MenuItem>
          <MenuItem value="half">Half width</MenuItem>
        </TextField>
      </Box>

      {needsOptions(field.type) ? (
        <TextField
          sx={{ mt: 2 }}
          fullWidth
          size="small"
          label="Options (comma-separated)"
          value={(field.options || []).join(', ')}
          onChange={(e) =>
            onChange(field.id, {
              options: e.target.value
                .split(',')
                .map((item) => item.trim())
                .filter(Boolean),
            })
          }
          helperText="Example: Android, iOS, Other"
        />
      ) : null}
    </Paper>
  );
}

export default function FeedbackFormBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [slug, setSlug] = useState('');
  const [status, setStatus] = useState('draft');
  const [fields, setFields] = useState([]);
  const [theme, setTheme] = useState(DEFAULT_THEME);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const response = await api_service.getFeedbackFormDetail(id);
        if (cancelled) return;
        if (response?.responseCode === 200 && response.data) {
          const form = response.data;
          setTitle(form.title || '');
          setDescription(form.description || '');
          setSlug(form.slug || '');
          setStatus(form.status || 'draft');
          setTheme({ ...DEFAULT_THEME, ...(form.theme || {}) });
          setFields(
            (form.fields || [])
              .slice()
              .sort((a, b) => a.order - b.order)
              .map((field, index) => ({
                ...field,
                id: field.id || createFieldId(),
                options: field.options || [],
                icon: field.icon || 'chat',
                iconColor: field.iconColor || '#7B08EF',
                badgeNumber: field.badgeNumber ?? null,
                width: field.width || 'full',
                order: index,
              }))
          );
        } else {
          toast.error(response?.error?.message || 'Form not found');
          navigate('/feedback-forms');
        }
      } catch (err) {
        toast.error(err?.message || 'Failed to load form');
        navigate('/feedback-forms');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  const updateField = (fieldId, patch) => {
    setFields((prev) =>
      prev.map((field) => {
        if (field.id !== fieldId) return field;
        const next = { ...field, ...patch };
        if (patch.type && !needsOptions(patch.type)) {
          next.options = [];
        }
        return next;
      })
    );
  };

  const updateTheme = (patch) => setTheme((prev) => ({ ...prev, ...patch }));

  const removeField = (fieldId) => {
    setFields((prev) =>
      prev.filter((field) => field.id !== fieldId).map((field, index) => ({ ...field, order: index }))
    );
  };

  const moveField = (from, to) => {
    setFields((prev) => arrayMove(prev, from, to).map((field, index) => ({ ...field, order: index })));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setFields((prev) => {
      const oldIndex = prev.findIndex((item) => item.id === active.id);
      const newIndex = prev.findIndex((item) => item.id === over.id);
      return arrayMove(prev, oldIndex, newIndex).map((field, index) => ({ ...field, order: index }));
    });
  };

  const applyPreset = (preset) => {
    if (preset === 'A') {
      setFields(PRESET_A_FIELDS());
      setTheme(PRESET_A_THEME);
      if (!title.trim()) setTitle('Share Your Experience');
      toast.info('Applied Design A (simple stacked form)');
    } else {
      setFields(PRESET_B_FIELDS());
      setTheme(PRESET_B_THEME);
      if (!title.trim()) setTitle('Share Your Experience');
      toast.info('Applied Design B (upload + rewards + side-by-side)');
    }
  };

  const validate = () => {
    if (!title.trim()) {
      toast.error('Title is required');
      return false;
    }
    for (const field of fields) {
      if (!field.label.trim()) {
        toast.error('Every field needs a label');
        return false;
      }
      if (needsOptions(field.type) && (!field.options || field.options.length === 0)) {
        toast.error(`"${field.label || field.type}" needs at least one option`);
        return false;
      }
      if (
        supportsMaxLength(field.type) &&
        field.maxLength != null &&
        field.maxLength !== '' &&
        (!Number.isFinite(Number(field.maxLength)) || Number(field.maxLength) < 1)
      ) {
        toast.error(`"${field.label}" character/digit limit must be a positive number`);
        return false;
      }
    }
    return true;
  };

  const handleSave = async (nextStatus) => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim(),
        slug: slug.trim() || undefined,
        status: nextStatus || status,
        theme,
        fields: fields.map((field, index) => ({
          id: field.id,
          type: field.type,
          label: field.label.trim(),
          placeholder: field.placeholder || '',
          required: Boolean(field.required),
          options: needsOptions(field.type) ? field.options : undefined,
          order: index,
          icon: field.icon || 'chat',
          iconColor: field.iconColor || '#7B08EF',
          badgeNumber: field.badgeNumber ?? null,
          width: field.width || 'full',
          maxLength:
            supportsMaxLength(field.type) && field.maxLength
              ? Number(field.maxLength)
              : null,
        })),
      };
      const response = await api_service.updateFeedbackForm({ id, data: payload });
      if (response?.responseCode === 200) {
        toast.success('Form saved');
        setStatus(response.data?.status || payload.status);
        setSlug(response.data?.slug || slug);
      } else {
        toast.error(response?.error?.message || 'Failed to save form');
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to save form');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!validate()) return;
    await handleSave('published');
    try {
      const response = await api_service.publishFeedbackForm(id);
      if (response?.responseCode === 200) {
        toast.success('Form published');
        setStatus('published');
        setSlug(response.data?.slug || slug);
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to publish');
    }
  };

  const copyPublicUrl = async () => {
    if (!slug) return;
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/f/${slug}`);
      toast.success('Public URL copied');
    } catch {
      toast.error('Could not copy URL');
    }
  };

  const copyTokenUrlExample = async () => {
    if (!slug) return;
    const example = `${window.location.origin}/f/${slug}?token=<USER_ID_TOKEN>`;
    try {
      await navigator.clipboard.writeText(example);
      toast.success('Token URL template copied (replace <USER_ID_TOKEN>)');
    } catch {
      toast.error('Could not copy URL');
    }
  };

  const addField = () => setFields((prev) => [...prev, newField(prev.length)]);

  if (loading) {
    return (
      <Box sx={{ m: '20px', mt: '100px', textAlign: 'left' }} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ m: '20px', mt: '100px', textAlign: 'left' }}>
      <Header title="Edit Feedback Form" subtitle="Configure fields, Rapchik theme, and publish status" />

      <Box
        display="flex"
        flexWrap="wrap"
        gap={1.5}
        alignItems="center"
        mb={3}
        mt={2}
        p={2}
        sx={{
          bgcolor: '#f5f5f5',
          borderRadius: 1,
          border: '1px solid #e0e0e0',
        }}
      >
        <Button variant="contained" color="secondary" onClick={() => navigate('/feedback-forms')}>
          Back
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => navigate(`/feedback-forms/${id}/responses`)}
        >
          View responses
        </Button>
        <Button variant="contained" color="secondary" onClick={copyPublicUrl} disabled={!slug}>
          Copy public URL
        </Button>
        <Button variant="outlined" color="secondary" onClick={copyTokenUrlExample} disabled={!slug}>
          Copy token URL
        </Button>
        <Box flexGrow={1} />
        <Button variant="contained" color="secondary" startIcon={<AddIcon />} onClick={addField}>
          Add field
        </Button>
        <Button variant="contained" color="secondary" disabled={saving} onClick={() => handleSave()}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
        <Button variant="contained" color="success" disabled={saving} onClick={handlePublish}>
          Publish
        </Button>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" mb={1}>
          Quick presets (from mockups)
        </Typography>
        <Typography color="text.secondary" mb={2} fontSize={14}>
          Design A = stacked form without upload/rewards. Design B = upload, side-by-side Device/Email,
          numbered badges, and rewards banner.
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={1.5}>
          <Button variant="outlined" color="secondary" onClick={() => applyPreset('A')}>
            Apply Design A
          </Button>
          <Button variant="outlined" color="secondary" onClick={() => applyPreset('B')}>
            Apply Design B
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={2}>
          <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} fullWidth required />
          <TextField
            label="Slug"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            fullWidth
            helperText={
              slug
                ? `Guest: ${window.location.origin}/f/${slug} · Authed (form-only): .../f/${slug}?token=<USER_ID_TOKEN>`
                : 'Used in public URL: /f/{slug} — add ?token= for user profile (no dashboard access)'
            }
          />
          <TextField
            label="Description (internal)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            sx={{ gridColumn: { md: '1 / -1' } }}
          />
          <TextField select label="Status" value={status} onChange={(e) => setStatus(e.target.value)} fullWidth>
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="published">Published</MenuItem>
            <MenuItem value="closed">Closed</MenuItem>
          </TextField>
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" mb={2}>
          Public page theme
        </Typography>
        <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={2}>
          <TextField
            label="Header title (white)"
            value={theme.headerTitle}
            onChange={(e) => updateTheme({ headerTitle: e.target.value })}
            fullWidth
            size="small"
          />
          <TextField
            label="Header highlight (yellow)"
            value={theme.headerHighlight}
            onChange={(e) => updateTheme({ headerHighlight: e.target.value })}
            fullWidth
            size="small"
          />
          <TextField
            label="Subtitle"
            value={theme.subtitle}
            onChange={(e) => updateTheme({ subtitle: e.target.value })}
            fullWidth
            size="small"
            helperText='e.g. "Help us make Rapchik better!" vs "even better!"'
          />
          <TextField
            label="Brand name"
            value={theme.brandName}
            onChange={(e) => updateTheme({ brandName: e.target.value })}
            fullWidth
            size="small"
          />
          <TextField
            label="Submit button text"
            value={theme.submitButtonText}
            onChange={(e) => updateTheme({ submitButtonText: e.target.value })}
            fullWidth
            size="small"
          />
          <TextField
            label="Footer text"
            value={theme.footerText}
            onChange={(e) => updateTheme({ footerText: e.target.value })}
            fullWidth
            size="small"
            helperText="Shown below the submit button"
          />
          <TextField
            label="Rewards banner text"
            value={theme.rewardsBannerText}
            onChange={(e) => updateTheme({ rewardsBannerText: e.target.value })}
            fullWidth
            size="small"
            sx={{ gridColumn: { md: '1 / -1' } }}
            disabled={!theme.showRewardsBanner}
          />
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(theme.showRewardsBanner)}
                onChange={(e) => updateTheme({ showRewardsBanner: e.target.checked })}
              />
            }
            label="Show rewards banner (Design B)"
          />
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(theme.showNumberedBadges)}
                onChange={(e) => updateTheme({ showNumberedBadges: e.target.checked })}
              />
            }
            label="Show numbered badges on fields"
          />
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(theme.showBackButton)}
                onChange={(e) => updateTheme({ showBackButton: e.target.checked })}
              />
            }
            label="Show back button"
          />
        </Box>
      </Paper>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Fields</Typography>
        <Button variant="contained" color="secondary" startIcon={<AddIcon />} onClick={addField}>
          Add field
        </Button>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {fields.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            p: 4,
            mb: 2,
            textAlign: 'left',
            borderStyle: 'dashed',
          }}
        >
          <Typography color="text.secondary" mb={2}>
            No fields yet. Use a preset above or add fields manually.
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            <Button variant="contained" color="secondary" onClick={() => applyPreset('A')}>
              Apply Design A
            </Button>
            <Button variant="contained" color="secondary" onClick={() => applyPreset('B')}>
              Apply Design B
            </Button>
            <Button variant="outlined" color="secondary" startIcon={<AddIcon />} onClick={addField}>
              Add field
            </Button>
          </Box>
        </Paper>
      ) : (
        <>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis]}
          >
            <SortableContext items={fields.map((field) => field.id)} strategy={verticalListSortingStrategy}>
              {fields.map((field, index) => (
                <SortableFieldCard
                  key={field.id}
                  field={field}
                  index={index}
                  total={fields.length}
                  onChange={updateField}
                  onRemove={removeField}
                  onMove={moveField}
                />
              ))}
            </SortableContext>
          </DndContext>
          <Box mt={1} mb={3}>
            <Button variant="contained" color="secondary" startIcon={<AddIcon />} onClick={addField}>
              Add another field
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
}
